import { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import hubLayout from 'styles/css/hubLayout.module.css';
import { Admin, CameraModel, Contract, ContractProductCount, Jisa, Model } from 'type/type';
import { useDaumPostcodePopup } from "react-daum-postcode";
import { bankList, cardCompanyList, calculDayDateToString, calculYearDateToString, contractStatusLabel } from 'pages/utils/commonUtils';
import { decryptAES256, deleteContract, getAdminList, getCameraModelAll, getContractByNo, getJisaAll, getModelAll, getRecorderByContractNo, maxLengthCheck, putContract } from 'api/api';
import Loading from 'components/common/Loading';
import { styled } from 'styled-components';
import { useRecoilState } from 'recoil';
import { isDarkAtom } from 'styles/atoms';
import { AdminLoginInfoContext } from 'pages/Layout';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;
  > div {
    padding: 0.5rem 1rem 1rem;
    &:not(:nth-child(odd)) {
      margin-bottom: 1rem;
      background-color: ${prop => prop.theme.layer3BgColor};
      border-radius: 0.5rem;
    }
  }
  details {
    background-color: ${({ theme }) => theme.newTheme.layer.layer2};
    border-radius: 0.5rem;
    summary {
      background-color: ${({ theme }) => theme.newTheme.root.secondary};
      padding: 0.25rem 1rem;
      border-radius: 0.5rem;
      &::-webkit-details-marker { /* Safari 브라우저용 사용자 정의 스타일 */
        display: none;
      }
      &::marker {
        font-size: 1.5rem;
      }
    }
  }
  .title-h2 {
    font-family: var(--font-pretendard-bold);
    display: inline-flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
  }

  label, input[name=text], select, textarea {
    display: inline-block;
    padding: 0.5rem;
  }
  input[name=text], select, textarea {
    border-radius: 0.25rem;
    font-family: var(--font-pretendard-medium);
    &:focus-visible {
      outline: none;
    }
  }
  input[name=text]:disabled {
    background-color: ${prop => prop.theme.readOnlyColor};
    color: ${prop => prop.theme.rootTextColor};
    cursor: auto;
  }
  dt {
    min-width: 100px;
  }
  dd {
    flex: none !important;
  }
`;
const TitleAndBtnContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  > div {
    display: flex;
    gap: 0.5rem;
    &:first-of-type {
      flex-direction: column;
    }
    &:last-of-type {
      align-items: center;
    }
  }
`;

// 다음주소 팝업창 theme
const themeObj = {
  bgColor: "#FFFFFF", //바탕 배경색
  searchBgColor: "#FFFFFF", //검색창 배경색
  contentBgColor: "#FFFFFF", //본문 배경색(검색결과,결과없음,첫화면,검색서제스트)
  pageBgColor: "#162525", //페이지 배경색
  textColor: "#162525", //기본 글자색
  queryTextColor: "#162525", //검색창 글자색
  outlineColor: "#444444" //테두리
};

function ContractRegister() {
  const navigate = useNavigate();
  const { adminLoginInfo } = useContext(AdminLoginInfoContext);
  const loginAuthCode = Array.isArray(adminLoginInfo.auth_code) ? adminLoginInfo.auth_code : []; // 배열로 변환
  const isAuthPermission = loginAuthCode.includes("C01") ? true : false;
  const { contractNo } = useParams();
  const [loading, setLoading] = useState(true);
  const [isEditUserId, setIsEditUserId] = useState(true);
  const darkAtom = useRecoilState(isDarkAtom);
  const [salesMobile, setSalesMobile] = useState<string>('');
  const [searchSalesValue, setSearchSalesValue] = useState<string>('');
  const [salesList, setSalesList] = useState<Admin[]|[]>([]);
  const { REACT_APP_SECRET_KEY, REACT_APP_SECRET_IV } = process.env;
  const [originStatus, setOriginStatus] = useState<string>('');
  const [isChangeStatus, setIsChangeStatus] = useState<boolean>(false);
  
  // useForm
  const { register, formState: { errors, isValid }, setValue, trigger } = useForm<any>({
    mode: 'all',
  });
  //2024-11-21
  const [contract, setContract] = useState<Contract>(Object);
  const [contractProductCount, setContractProductCount] = useState<ContractProductCount>(Object);


  const [jisaList, setJisaList] = useState<Jisa[]>([]);
  const [modelList, setModelList] = useState<Model[]>([]);
  const [cameraModelList, setCameraModelList] = useState<CameraModel[]>([]);
  
  const getJisa = async () => {
    const response = await getJisaAll(0, 0);
    if (response.code !== 200) return;
  
    // `JsonValue`가 배열인지 체크하고 string[]로 변환
    const jisaCodeList = Array.isArray(adminLoginInfo.jisa_code)
      ? (adminLoginInfo.jisa_code as string[])
      : typeof adminLoginInfo.jisa_code === "string"
      ? [adminLoginInfo.jisa_code] // 문자열이면 배열로 변환
      : [];
  
    const filteredJisa = response.data.filter((jisa: Jisa) =>
      jisaCodeList.includes(jisa.jisa_code)
    );
  
    if (filteredJisa.length > 0) {
      setContract((prev) => ({ ...prev, jisa_code: filteredJisa[0].jisa_code }));
    }
  
    setJisaList(filteredJisa);
  };

  const getModel = async () => {
    const modelRes = await getModelAll(0, 0);
    if (modelRes.code === 200) {
      // name 기준으로 오름차순 정렬
      const sortedModelList = modelRes.data.sort((a: Model, b: Model) => a.name.localeCompare(b.name));
      setModelList(sortedModelList);
    }
  
    const cameraModelRes = await getCameraModelAll(0, 0);
    if (cameraModelRes.code === 200) {
      // name 기준으로 오름차순 정렬
      const sortedCameraModelList = cameraModelRes.data.sort((a: CameraModel, b: CameraModel) => a.name.localeCompare(b.name));
      setCameraModelList(sortedCameraModelList);
    }
  };

  // 녹화기 추가 처리 함수
  const handleAddRecorder = () => {
    setContractProductCount(prevState => ({
      ...prevState,
      recorder: prevState.recorder
        ? [...prevState.recorder, { model: "", use: 1 }] // 새로운 녹화기 추가
        : [{ model: "", use: 1 }], // 처음 추가할 경우 배열 생성
    }));
  };

  // 녹화기 제거 처리 함수
  const handleDeleteRecorder = (idx: number) => {
    setContractProductCount(prevState => ({
      ...prevState,
      recorder: prevState.recorder
        ? prevState.recorder.filter((_, i) => i !== idx) // 해당 idx 삭제
        : [],
    }));
  };

  // 모델 변경 처리 함수
  const handleModelChangeRecorder = (e: React.ChangeEvent<HTMLSelectElement>, idx: number) => {
    const updatedRecorder = [...contractProductCount.recorder!];
    updatedRecorder[idx] = {
      ...updatedRecorder[idx],
      model: e.target.value, // 선택된 모델로 변경
    };

    setContractProductCount(prevState => ({
      ...prevState,
      recorder: updatedRecorder, // 변경된 recorder 배열로 업데이트
    }));
  };

  // 사용 대수 변경 처리 함수
  const handleUseChangeRecorder = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
    const input = e.target.value;
    // 숫자만 추출
    const numericValue = input.replace(/[^0-9]/g, "");
    e.target.value = numericValue;

    const updatedRecorder = [...contractProductCount.recorder!];
    updatedRecorder[idx] = {
      ...updatedRecorder[idx],
      use: Number(e.target.value), // 입력된 대수로 업데이트
    };

    setContractProductCount(prevState => ({
      ...prevState,
      recorder: updatedRecorder, // 변경된 recorder 배열로 업데이트
    }));
  };

  // 녹화기 추가 처리 함수
  const handleAddCamera = () => {
    setContractProductCount(prevState => ({
      ...prevState,
      camera: prevState.camera
        ? [...prevState.camera, { model: "", use: 1 }] // 새로운 녹화기 추가
        : [{ model: "", use: 1 }], // 처음 추가할 경우 배열 생성
    }));
  };

  // 녹화기 제거 처리 함수
  const handleDeleteCamera = (idx: number) => {
    setContractProductCount(prevState => ({
      ...prevState,
      camera: prevState.camera
        ? prevState.camera.filter((_, i) => i !== idx) // 해당 idx 삭제
        : [],
    }));
  };

  // 모델 변경 처리 함수
  const handleModelChangeCamera = (e: React.ChangeEvent<HTMLSelectElement>, idx: number) => {
    const updatedCamera = [...contractProductCount.camera!];
    updatedCamera[idx] = {
      ...updatedCamera[idx],
      model: e.target.value, // 선택된 모델로 변경
    };

    setContractProductCount(prevState => ({
      ...prevState,
      camera: updatedCamera, // 변경된 camera 배열로 업데이트
    }));
  };

  // 사용 대수 변경 처리 함수
  const handleUseChangeCamera = (e: React.ChangeEvent<HTMLInputElement>, idx: number) => {
    const input = e.target.value;
    // 숫자만 추출
    const numericValue = input.replace(/[^0-9]/g, "");
    e.target.value = numericValue;

    const updatedCamera = [...contractProductCount.camera!];
    updatedCamera[idx] = {
      ...updatedCamera[idx],
      use: Number(e.target.value), // 입력된 대수로 업데이트
    };

    setContractProductCount(prevState => ({
      ...prevState,
      camera: updatedCamera, // 변경된 recorder 배열로 업데이트
    }));
  };

  // 계약 onChange 이벤트
  const handleChange = async (event: any) => {
    const { name, value } = event.target;

    // 입력이 빈 문자열일 때 처리 (날짜 필드의 경우)
    if (["contract_date", "start_date"].includes(name) && value === '') return;

    // 가격 처리
    const handlePrice = (value: string) => {
      let numVal = Number(value.replaceAll(',', ''));
      return Math.min(numVal, 2000000000);
    };

    // 계약 상태 처리
    const handleContractStatus = async (name: string, value: string) => {
      const isRestrictedStatus = ['A', 'B', 'G'].includes(value);

      if (!isEditUserId && isRestrictedStatus) {
        alert('해당 계약번호에는 장비가 등록되어있습니다. 장비 삭제 후 상태변경이 가능합니다.');
        return;
      }

      if (contract.user_id && isEditUserId && isRestrictedStatus) {
        if (window.confirm(`${contract.user_id}계정과 계약번호 매칭을 해제하시면 해당 계정은 삭제됩니다. 계약 매칭을 해제하시겠습니까?`)) {
          setContract((prevData) => ({
            ...prevData,
            user_id: '',
            [name]: value,
          }));
          setValue('user_id', '', { shouldValidate: false });
        }
      } else {
        setContract((prevData) => ({
          ...prevData,
          [name]: value,
        }));
      }
      await trigger();
    };

    // 계약 기간 처리
    const handleTerm = (value: string) => {
      let endDate = calculYearDateToString(new Date(contract.start_date), Number(value));
      return calculDayDateToString(new Date(endDate), -1);
    };

    // 서비스 코드 처리
    const handleServiceCode = (name: string, value: string) => ({
      service_code: {
        [name]: Number(value),
      },
    });

    // 시작일 처리
    const handleStartDate = (value: string) => {
      let endDate = calculYearDateToString(new Date(value), Number(contract.term));
      return calculDayDateToString(new Date(endDate), -1);
    };

    // 상태 업데이트용 임시 객체
    let updatedData: any = {};

    // 조건별 처리
    if (name === "price") {
      updatedData[name] = handlePrice(value);
    } else if (name === "contract_status") {
      await handleContractStatus(name, value);
      return;
    } else if (name === "term") {
      updatedData[name] = value;
      updatedData.end_date = handleTerm(value);
    } else if (name.includes('_service')) {
      updatedData = handleServiceCode(name, value);
    } else if (name.includes('is_send_')) {
      updatedData[name] = Number(value);
    } else {
      if (name === "contract_no") {
        setContractProductCount((prevData) => ({
          ...prevData,
          [name]: value,
        }));
      }
      if (name === "start_date") {
        updatedData[name] = value;
        updatedData.end_date = handleStartDate(value);
      }
      updatedData[name] = value;
    }

    // 상태 업데이트
    setContract((prevData) => ({
      ...prevData,
      ...updatedData,
    }));
  };

  const _editContract = async () => {
    if (!isValid) {
      alert("모든정보를 입력해 주세요.");
      return await trigger();
    }
    if (!['D', 'G'].includes(contract.contract_status)) {
      contract.status_reasons = "";
    }
    if (contract.contract_status === 'B') {
      const check = window.confirm('계약 승인 시 계약자/사업자 연락처로 임시 아이디와 모바일 APP 이용 안내 알림톡이 전송됩니다.');
      if (!check) return;
    }
    contract.contract_product_count = contractProductCount;
    const response = await putContract(contract);
    if (response.code === 200) {
      alert("계약정보가 수정되었습니다.")
      navigate(-1);
    } else {
      alert(response.message);
    }
  }

  const _deleteContract = async () => {
    var message: string;
    message = contract.user_id 
                ? '매칭되어있는 고객 계정 또는 녹화기가 있습니다. 삭제 시 복원이 불가능 합니다. 정말로 삭제하시겠습니까?'
                : '정말로 삭제하시겠습니까?';
    const isDel = window.confirm(message);
    if (!isDel) return;

    const delRes = await deleteContract(contract.idx);
    if (delRes.code === 200) {
      alert("계약정보가 삭제되었습니다.")
      navigate(-1);
    } else {
      alert(delRes.message);
    }
  }

  function handleToNumericValue(event: any) {
    const input = event.target.value;
    // 숫자만 추출
    const numericValue = input.replace(/[^0-9]/g, "");
    event.target.value = numericValue;
    handleChange(event);
  }

  const [birthdayFirst, setBirthdayFirst] = useState<string>("");
  const [birthdayLast, setBirthdayLast] = useState<string>("");
  function birthdayHandler(event: any) {
    const { name, value } = event.target;
    const numericValue = value.replace(/[^0-9]/g, "");
    let birthday_first:string = '';
    let birthday_last:string = '';
    if (name === 'birthday_first') {
      birthday_first = numericValue;
      birthday_last = birthdayLast;
      setBirthdayFirst(numericValue);
    } else if (name === 'birthday_last') {
      birthday_first = birthdayFirst;
      birthday_last = numericValue;
      setBirthdayLast(numericValue);
    }
    handleChange({ target : { name : 'birthday', value : birthday_first + birthday_last } });
  }
  
  const [businessNumber1, setBusinessNumber1] = useState<string>("");
  const [businessNumber2, setBusinessNumber2] = useState<string>("");
  const [businessNumber3, setBusinessNumber3] = useState<string>("");
  function businessNumberHandler(event: any) {
    const { name, value } = event.target;
    const numericValue = value.replace(/[^0-9]/g, "");
    let business_number1:string = '';
    let business_number2:string = '';
    let business_number3:string = '';
    if (name === 'business_number1') {
      business_number1 = numericValue;
      business_number2 = businessNumber2;
      business_number3 = businessNumber3;
      setBusinessNumber1(numericValue);
    } else if (name === 'business_number2') {
      business_number1 = businessNumber1;
      business_number2 = numericValue;
      business_number3 = businessNumber3;
      setBusinessNumber2(numericValue);
    } else if (name === 'business_number3') {
      business_number1 = businessNumber1;
      business_number2 = businessNumber2;
      business_number3 = numericValue;
      setBusinessNumber3(numericValue);
    }
    handleChange({ target : { name : 'business_number', value : business_number1 + business_number2 + business_number3 } });
  }
  
  // 주소 검색
  const open = useDaumPostcodePopup();
  function openDaumPostcode(data: any, name: string) {
    const zoneCode = data.zonecode; // 우편번호
    const roadAddress:string = data.roadAddress; // 도로명주소
    const addressName = name === 'company' ? 'company_address' : 'address';
    const postCodeName = name === 'company' ? 'company_post_code' : 'post_code';
    
    handleChange({ target : { name : addressName, value : roadAddress } });
    handleChange({ target : { name : postCodeName, value : zoneCode } });
    // react-hook-form의 setValue 사용
    setValue(postCodeName, zoneCode, { shouldValidate: true });
    setValue(addressName, roadAddress, { shouldValidate: true });
  }
  function handlerOpenDaum(name: string) {
    open({ 
      onComplete: (data) => openDaumPostcode(data, name), 
      theme: themeObj 
    });
  }

  const decryptData = (data: any, secretKey: string, secretIv: string) => {
    return {
      mobile: decryptAES256(data.mobile, secretKey, secretIv),
      company_mobile: decryptAES256(data.company_mobile, secretKey, secretIv),
      bank_name: decryptAES256(data.bank_name, secretKey, secretIv),
      bank_number: decryptAES256(data.bank_number, secretKey, secretIv),
      card_number: decryptAES256(data.card_number, secretKey, secretIv),
      card_company: decryptAES256(data.card_company, secretKey, secretIv)
    };
  };
  
  const formatBusinessNumber = (businessNumber: string) => {
    return {
      first: businessNumber.slice(0, 3),
      second: businessNumber.slice(3, 5),
      third: businessNumber.slice(5, 10)
    };
  };
  
  const getContracts = async () => {
    setLoading(true);
    
    if (!contractNo) {
      alert('잘못된 접근입니다.');
      navigate(-1);
      return;
    }
  
    try {
      await Promise.all([getModel(), getJisa()]);
      
      const response = await getContractByNo(contractNo);
      
      if (response.code !== 200) {
        alert(response.message);
        return;
      }
  
      const responseRecorderByContractNo = await getRecorderByContractNo(response.data.contract_no);
      
      if (responseRecorderByContractNo.code === 200) {
        setIsEditUserId(false);
      }
  
      // 복호화
      const decryptedData = decryptData(response.data, REACT_APP_SECRET_KEY!, REACT_APP_SECRET_IV!);
  
      setBirthdayFirst(response.data.birthday.slice(0, 6));
      setBirthdayLast(response.data.birthday.slice(6));
  
      const { first, second, third } = formatBusinessNumber(response.data.business_number);
      setBusinessNumber1(first);
      setBusinessNumber2(second);
      setBusinessNumber3(third);
      setContract({
        ...response.data,
        ...decryptedData,
        is_send_sales: 0,
        is_send_user: 0,
        is_send_company: 0,
      });
      setOriginStatus(response.data.contract_status);
      setContractProductCount(response.data.contract_product_count);
  
      const adminRes = await getAdminList(0, 0, { id: response.data.sales_id });
      if (adminRes.code === 200) {
        setSalesList(adminRes.data);
        setSalesMobile(decryptAES256(adminRes.data[0].mobile, REACT_APP_SECRET_KEY!, REACT_APP_SECRET_IV!));
      }
  
    } catch (error) {
      console.error(error);
      alert('오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  async function searchSales() {
    // 영업자 확인 API 필요
    const param = {
      id: searchSalesValue,
      name: searchSalesValue,
      auth_group: ["SA", "S"],
      type: "OR"
    }
    
    const adminRes = await getAdminList(0, 0, param);
    if (adminRes.code === 200) {
      if (adminRes.total_count === 0) {
        return alert("존재하지 않는 ID/이름 입니다.");
      }
      alert("영업자가 조회되었습니다.");
      setSalesList(adminRes.data);
    } else {
      alert("존재하지 않는 ID/이름 입니다.");
      setSalesList([]);
    }
  }
  useEffect(() => {
    getContracts();
  },[adminLoginInfo]);
  useEffect(() => {
    setIsChangeStatus(!(originStatus === contract.contract_status));
  }, [originStatus, contract.contract_status]);
  return (
    <>
      {loading ? <Loading /> :
        <Container>
          <TitleAndBtnContainer>
            <div>
              <p className='title-h2'>
                <span>* 계약 상태</span>
              </p>
            </div>
            <div>
              <button type="button" className='default-btn delete-btn' onClick={_deleteContract}>삭제</button>
              <button type="button" className='default-btn edit-btn' onClick={_editContract}>수정</button>
              <button type="button" className='default-btn list-btn' onClick={() => navigate(-1)}>목록</button>
            </div>
          </TitleAndBtnContainer>
          <div className={`${hubLayout.acc__cnt}`}>
            <div className={hubLayout.acc_item_info}>
              <dl>
                <dd>
                  {contractStatusLabel.map(({ code, name }) => 
                    <span key={code} className={hubLayout.margin_right_10}>
                      <input
                        type="radio"
                        className={hubLayout.radio}
                        value={code}
                        id={`inp-status-${code}`}
                        checked={contract.contract_status === code}
                        style={{ top: '7px' }}
                        readOnly={!isAuthPermission}
                        {...register("contract_status", {
                          required: true,
                          onChange: handleChange,
                        })}
                      />
                      <label htmlFor={`inp-status-${code}`} style={{ color: darkAtom ? '#fff' : '' }}>
                        {name}
                      </label>
                    </span>
                  )}
                </dd>
              </dl>
              {(contract.contract_status === 'C' ||
                ((['D', 'E', 'F'].includes(contract.contract_status)) && contract.user_id)) && (
                  <dl>
                    <dt style={{ width: '8rem' }}>매칭 ID</dt>
                    <dd>
                      <input
                        type="text"
                        className={`${errors.user_id && hubLayout.border_red}`}
                        placeholder="매칭 ID"
                        value={contract.user_id || ""}
                        {...register("user_id", {
                          required: {
                            value: true,
                            message: '매칭 ID를 입력하세요',
                          },
                          onChange: handleChange,
                        })}
                        readOnly={!isEditUserId}
                      />
                    </dd>
                  </dl>
                )
              }
              {(errors.user_id) && (
                <dl>
                  <dt style={{ width: '8rem' }}></dt>
                  <p className={hubLayout.tx_red}>
                    {String(errors.user_id?.message)}
                  </p>
                </dl>
              )}

              {(['D', 'G'].includes(contract.contract_status)) && (
                <dl>
                  <dt style={{ width: '8rem' }}>사유</dt>
                  <textarea
                    id="status_reasons"
                    placeholder="사유를 입력하세요."
                    value={contract.status_reasons}
                    className={`${errors.status_reasons && hubLayout.border_red}`}
                    maxLength={500}
                    style={{ width: '30rem' }}
                    {...register("status_reasons", {
                      required: {
                        value: true,
                        message: '사유를 입력하세요',
                      },
                      onChange: (e) => {
                        maxLengthCheck(e);
                        handleChange(e);
                      }
                    })}
                  ></textarea>
                </dl>
              )}
              {(errors.status_reasons) && (
                <dl>
                  <dt style={{ width: '8rem' }}></dt>
                  <p className={hubLayout.tx_red}>
                    {String(errors.status_reasons?.message)}
                  </p>
                </dl>
              )}
            </div>
            {isChangeStatus && (
              <div className={hubLayout.acc_item_info} style={{ marginTop: "3rem" }}>
                <dl>
                  <dt style={{ width: 'auto' }}>
                    <p><b>* 계약 상태가 변경되었습니다. 알림 대상과 알림 여부를 선택 해 주세요.</b></p>
                    {contract.contract_status === 'B' && <p><b>* 승인 상태로 변경된 경우 고객 및 사업장 연락처로 임시 아이디가 전송됩니다.</b></p>}
                  </dt>
                </dl>

                {/* 고객 */}
                {contract.contract_status === 'B' && (
                  <>
                    <dl>
                      <dt style={{ width: 'auto' }}>고객</dt>
                      <dd>
                        {[1, 0].map((val) => (
                          <span key={val} className={hubLayout.margin_right_10}>
                            <input
                              type="radio"
                              className={hubLayout.radio}
                              value={val}
                              id={`inp-user-${val}`}
                              checked={contract.is_send_user === val}
                              style={{ top: '7px' }}
                              {...register('is_send_user', {
                                onChange: handleChange,
                              })}
                            />
                            <label htmlFor={`inp-user-${val}`} style={{ color: darkAtom ? '#fff' : '' }}>
                              {val === 1 ? '전송' : '미전송'}
                            </label>
                          </span>
                        ))}
                      </dd>
                    </dl>

                    {/* 사업장 */}
                    {contract.company_mobile && (
                      <dl>
                        <dt style={{ width: 'auto' }}>사업장</dt>
                        <dd>
                          {[1, 0].map((val) => (
                            <span key={val} className={hubLayout.margin_right_10}>
                              <input
                                type="radio"
                                className={hubLayout.radio}
                                value={val}
                                id={`inp-company-${val}`}
                                checked={contract.is_send_company === val}
                                style={{ top: '7px' }}
                                {...register('is_send_company', {
                                  onChange: handleChange,
                                })}
                              />
                              <label htmlFor={`inp-company-${val}`} style={{ color: darkAtom ? '#fff' : '' }}>
                                {val === 1 ? '전송' : '미전송'}
                              </label>
                            </span>
                          ))}
                        </dd>
                      </dl>
                    )}
                  </>
                )}

                {/* 영업자 */}
                <dl>
                  <dt style={{ width: 'auto' }}>영업자</dt>
                  <dd>
                    {[1, 0].map((val) => (
                      <span key={val} className={hubLayout.margin_right_10}>
                        <input
                          type="radio"
                          className={hubLayout.radio}
                          value={val}
                          id={`inp-sales-${val}`}
                          checked={contract.is_send_sales === val}
                          style={{ top: '7px' }}
                          {...register('is_send_sales', {
                            onChange: handleChange,
                          })}
                        />
                        <label htmlFor={`inp-sales-${val}`} style={{ color: darkAtom ? '#fff' : '' }}>
                          {val === 1 ? '전송' : '미전송'}
                        </label>
                      </span>
                    ))}
                  </dd>
                </dl>
              </div>
            )}
          </div>

          <TitleAndBtnContainer>
            <div>
              <p className='title-h2'>
                <span>* 기본 정보</span>
              </p>
            </div>
          </TitleAndBtnContainer>
          <div className={`${hubLayout.acc__cnt}`}>
            <div className={`${hubLayout.acc_item_info}`}>
              <dl>
                <dt><label>이름</label></dt>
                <dd>
                  <input type="text" value={contract.name} placeholder="이름을 입력하세요." className={`${errors.name && hubLayout.border_red}`}
                    {...register("name", {
                      required: {
                        value: true,
                        message: '이름을 입력하세요',
                      },
                      onChange: handleChange
                    })}
                  />
                  {errors.name && <p className={`${hubLayout.tx_red}`}>{String(errors.name.message)}</p>}
                </dd>
                <dt><label>연락처</label></dt>
                <dd>
                  <input type='text' value={contract.mobile || ""} maxLength={11} placeholder='ex) 01012345678' className={`${errors.mobile && hubLayout.border_red}`}
                    {...register(`mobile`, {
                      required: {
                        value: true,
                        message: '연락처를 입력하세요',
                      },
                      pattern : {
                        value: /^01[016789]\d{3,4}\d{4}$/g,
                        message: `정확한 연락처를 입력하세요`,
                      },
                      onChange: (e:any) => {
                        maxLengthCheck(e);
                        handleToNumericValue(e);
                      }
                    })}
                  />
                  {errors.mobile && <p className={`${hubLayout.tx_red}`}>{String(errors.mobile.message)}</p>}
                </dd>
              </dl>
              <dl>
                <dt><label>E-Mail</label></dt>
                <dd>
                  <input type='text' value={contract.email || ""} maxLength={100} placeholder='test@naver.com' className={`${errors.email && hubLayout.border_red}`}
                    {...register(`email`, {
                      required: {
                        value: true,
                        message: 'E-Mail을 입력하세요',
                      },
                      pattern : {
                        value: /^[a-zA-Z0-9+-\_.]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/g,
                        message: `정확한 E-Mail을 입력하세요`,
                      },
                      onChange: (e:any) => {
                        maxLengthCheck(e);
                        handleChange(e);
                      }
                    })}
                  />
                  {errors.email && <p className={`${hubLayout.tx_red}`}>{String(errors.email.message)}</p>}
                </dd>
                <dt><label>생년월일</label></dt>
                <dd>
                  <input type='text' placeholder='생년월일 6자리' value={birthdayFirst || ""} style={{ width: '40%' }} maxLength={6} className={`${errors.birthday_first && hubLayout.border_red}`}
                    {...register(`birthday_first`, {
                      required: {
                        value: true,
                        message: '생년월일을 입력하세요',
                      },
                      pattern : {
                        value: /([0-9]{2}(0[1-9]|1[0-2])(0[1-9]|[1,2][0-9]|3[0,1]))/g,
                        message: `정확한 생년월일을 입력하세요.`,
                      },
                      onChange: (e:any) => {
                        maxLengthCheck(e);
                        birthdayHandler(e);
                      }
                    })}
                  />
                  <span style={{ color: darkAtom ? '#fff' : '#000', margin: '0 5px' }}>-</span>
                  <input type='text' value={birthdayLast || ""} style={{ width: '15%', textAlign: 'center' }} maxLength={1} className={`${errors.birthday_last && hubLayout.border_red}`}
                    {...register(`birthday_last`, {
                      required: {
                        value: true,
                        message: '생년월일을 입력하세요.',
                      },
                      pattern : {
                        value: /^[1-4]{1}$/g,
                        message: `정확한 생년월일을 입력하세요.`,
                      },
                      onChange: (e:any) => {
                        maxLengthCheck(e);
                        birthdayHandler(e);
                      }
                    })}
                  />
                  <span style={{ color: darkAtom ? '#fff' : '#000' }}> * * * * * *</span>
                  {(errors.birthday_first || errors.birthday_last) && 
                    <p className={`${hubLayout.tx_red}`}>
                      {String(errors.birthday_first?.message || errors.birthday_last?.message)}
                    </p>
                  }
                </dd>
              </dl>
              <dl>
                <dt><label>주소</label></dt>
                <dd className={`${hubLayout.display_flex}`}>
                  <input type="text" className={`${hubLayout.width_100} ${hubLayout.margin_right_10} ${errors.post_code && hubLayout.border_red}`} readOnly
                    placeholder="우편번호" value={contract.post_code}
                    {...register(`post_code`, {
                      required: {
                        value: true,
                        message: '주소를 검색하세요.',
                      },
                    })}
                  />
                  <button type="button" className={`${hubLayout.btn} ${hubLayout.width_150} ${hubLayout.margin_right_10} ${hubLayout.medium} ${hubLayout.blue}`} onClick={() => handlerOpenDaum('')}>검색</button>
                  <input type="text" placeholder="주소를 검색하세요." value={contract.address} className={`${hubLayout.margin_right_10} ${errors.address && hubLayout.border_red}`} readOnly
                    {...register(`address`, {
                      required: {
                        value: true,
                        message: '주소를 검색하세요.',
                      },
                    })}
                  />
                  <input type="text" placeholder="상세주소 입력" value={contract.address_detail} className={`${errors.address_detail && hubLayout.border_red}`}
                    {...register(`address_detail`, {
                      required: {
                        value: true,
                        message: '상세 주소를 입력하세요.',
                      },
                      onChange: (e:any) => handleChange(e)
                    })}
                  />
                </dd>
                <dd style={{ marginLeft: '10px' }}>
                  {(errors.post_code || errors.address || errors.address_detail) && 
                    <p className={`${hubLayout.tx_red}`}>
                      {String(errors.post_code?.message || errors.address?.message || errors.address_detail?.message)}
                    </p>
                  }
                </dd>
              </dl>
            </div>
          </div>

          <TitleAndBtnContainer>
            <div>
              <p className='title-h2'>
                <span>사업장 정보 <span style={{ fontSize: '12px' }}>(개인일 경우 Skip)</span></span>
              </p>
            </div>
          </TitleAndBtnContainer>
          <div className={`${hubLayout.acc__cnt}`}>
            <div className={`${hubLayout.acc_item_info}`}>
              <dl>
                <dt><label>사업자번호</label></dt>
                <dd>
                  <input type='text' value={businessNumber1} style={{ width: '20%' }} maxLength={3} className={`${errors.business_number1 && hubLayout.border_red}`}
                    {...register(`business_number1`, {
                      required: {
                        value: false,
                        message: '사업자번호를 입력하세요.',
                      },
                      pattern : {
                        value: /^[0-9]{3}$/g,
                        message: `정확한 ${'사업자번호를 입력하세요.'}`,
                      },
                      onChange: (e:any) => {
                        maxLengthCheck(e);
                        businessNumberHandler(e);
                      }
                    })}
                  />
                  <span style={{ color: darkAtom ? '#fff' : '#000', margin: '0 5px' }}>-</span>
                  <input type='text' value={businessNumber2 || ""} style={{ width: '10%' }} maxLength={2} className={`${errors.business_number2 && hubLayout.border_red}`}
                    {...register(`business_number2`, {
                      required: {
                        value: false,
                        message: '사업자번호를 입력하세요.',
                      },
                      pattern : {
                        value: /^[0-9]{2}$/g,
                        message: `정확한 사업자번호를 입력하세요.`,
                      },
                      onChange: (e:any) => {
                        maxLengthCheck(e);
                        businessNumberHandler(e);
                      }
                    })}
                  />
                  <span style={{ color: darkAtom ? '#fff' : '#000', margin: '0 5px' }}>-</span>
                  <input type='text' value={businessNumber3 || ""} style={{ width: '30%' }} maxLength={5} className={`${errors.business_number3 && hubLayout.border_red}`}
                    {...register(`business_number3`, {
                      required: {
                        value: false,
                        message: '사업자번호를 입력하세요.',
                      },
                      pattern : {
                        value: /^[0-9]{5}$/g,
                        message: `정확한 사업자번호를 입력하세요.`,
                      },
                      onChange: (e:any) => {
                        maxLengthCheck(e);
                        businessNumberHandler(e);
                      }
                    })}
                  />
                  {(errors.business_number1 || errors.business_number2 || errors.business_number3) && 
                    <p className={`${hubLayout.tx_red}`}>
                      {String(errors.business_number1?.message || errors.business_number2?.message || errors.business_number3?.message)}
                    </p>
                  }
                </dd>
              </dl>
              <dl>
                <dt><label>상호</label></dt>
                <dd>
                  <input type="text" value={contract.company_name} placeholder="상호를 입력하세요." className={`${errors.company_name && hubLayout.border_red}`}
                    {...register("company_name", {
                      required: false,
                      onChange: handleChange
                    })}
                  />
                  {errors.company_name && <p className={`${hubLayout.tx_red}`}>{String(errors.company_name.message)}</p>}
                </dd>
                <dt><label>회사연락처</label></dt>
                <dd>
                  <input type='text' value={contract.company_mobile} maxLength={11} placeholder='ex) 01012345678' className={`${errors.company_mobile && hubLayout.border_red}`}
                    {...register(`company_mobile`, {
                      required: {
                        value: false,
                        message: '연락처를 입력하세요',
                      },
                      pattern : {
                        value: /^01[016789]\d{3,4}\d{4}$/g,
                        message: `정확한 연락처를 입력하세요`,
                      },
                      onChange: (e:any) => {
                        maxLengthCheck(e);
                        handleToNumericValue(e);
                      }
                    })}
                  />
                  {errors.company_mobile && <p className={`${hubLayout.tx_red}`}>{String(errors.company_mobile.message)}</p>}
                </dd>
              </dl>
              <dl>
                <dt><label>업태</label></dt>
                <dd>
                  <input type="text" value={contract.business_type} placeholder="업태를 입력하세요." className={`${errors.business_type && hubLayout.border_red}`}
                    {...register("business_type", {
                      required: {
                        value: false,
                        message: '업태를 입력하세요.',
                      },
                      onChange: handleChange
                    })}
                  />
                  {errors.business_type && <p className={`${hubLayout.tx_red}`}>{String(errors.business_type.message)}</p>}
                </dd>
                <dt><label>종목</label></dt>
                <dd>
                  <input type="text" value={contract.business_items} placeholder="종목을 입력하세요." className={`${errors.business_items && hubLayout.border_red}`}
                    {...register("business_items", {
                      required: {
                        value: false,
                        message: '종목을 입력하세요.',
                      },
                      onChange: handleChange
                    })}
                  />
                  {errors.business_items && <p className={`${hubLayout.tx_red}`}>{String(errors.business_items.message)}</p>}
                </dd>
              </dl>
              <dl>
                <dt><label>프랜차이즈</label></dt>
                <dd>
                  <input type="text" value={contract.franchise} placeholder="프랜차이즈명을 입력하세요." className={`${errors.franchise && hubLayout.border_red}`}
                    {...register("franchise", {
                      required: false,
                      onChange: handleChange
                    })}
                  />
                </dd>
                <dt><label>기존업체</label></dt>
                <dd>
                  <input type="text" value={contract.bef_company} placeholder="기존업체명을 입력하세요." className={`${errors.bef_company && hubLayout.border_red}`}
                    {...register("bef_company", {
                      required: false,
                      onChange: handleChange
                    })}
                  />
                </dd>
              </dl>
              <dl>
              </dl>
              <dl>
                <dt><label>사업장주소</label></dt>
                <dd className={`${hubLayout.display_flex}`}>
                  <input type="text" className={`${hubLayout.width_100} ${hubLayout.margin_right_10} ${errors.company_post_code && hubLayout.border_red}`} readOnly
                    placeholder="우편번호" value={contract.company_post_code}
                    {...register(`company_post_code`, {
                      required: {
                        value: false,
                        message: '주소를 검색하세요.',
                      },
                    })}
                  />
                  <button type="button" className={`${hubLayout.btn} ${hubLayout.width_150} ${hubLayout.margin_right_10} ${hubLayout.medium} ${hubLayout.blue}`} onClick={() => handlerOpenDaum('company')}>검색</button>
                  <input type="text" placeholder="주소를 검색하세요." value={contract.company_address} className={`${hubLayout.margin_right_10} ${errors.company_address && hubLayout.border_red}`} readOnly
                    {...register(`company_address`, {
                      required: {
                        value: false,
                        message: '주소를 검색하세요.',
                      },
                    })}
                  />
                  <input type="text" placeholder="상세주소 입력" value={contract.company_address_detail} className={`${errors.company_address_detail && hubLayout.border_red}`}
                    {...register(`company_address_detail`, {
                      required: {
                        value: false,
                        message: '상세 주소를 입력하세요.',
                      },
                      onChange: handleChange
                    })}
                  />
                </dd>
                <dd style={{ marginLeft: '10px' }}>
                  {(errors.company_post_code || errors.company_address || errors.company_address_detail) && 
                    <p className={`${hubLayout.tx_red}`}>
                      {String(errors.company_post_code?.message || errors.company_address?.message || errors.company_address_detail?.message)}
                    </p>
                  }
                </dd>
              </dl>
            </div>
          </div>

          <TitleAndBtnContainer>
            <div>
              <p className='title-h2'>
                <span>* 계약 정보</span>
              </p>
            </div>
          </TitleAndBtnContainer>
          <div className={`${hubLayout.acc__cnt}`}>
            <div className={`${hubLayout.acc_item_info}`}>
              <dl>
                <dt><label>계약번호</label></dt>
                <dd>
                  <input type='text' value={contract.contract_no} maxLength={9} placeholder='계약번호를 입력하세요.' disabled={true}
                    {...register(`contract_no`)}
                  />
                  {errors.contract_no && <p className={`${hubLayout.tx_red}`}>{String(errors.contract_no.message)}</p>}
                </dd>
              </dl>
              <dl>
                <dt><label>영업자</label></dt>
                <dd className={`${hubLayout.display_flex}`}>
                  <input type="text" value={searchSalesValue} placeholder="ID 또는 영업자명을 입력하세요." className={`${errors.id && hubLayout.border_red}`}
                    {...register("id", {
                      onChange: (e:any) => {
                        setSearchSalesValue(e.target.value);
                      }
                    })}
                  />
                  <button type="button" className={`${hubLayout.btn} ${hubLayout.medium} ${hubLayout.blue} ${hubLayout.width_100}`} onClick={searchSales}>조회</button>
                </dd>
                <dt><label>영업자 ID</label></dt>
                <dd>
                  <div className={`${darkAtom ? hubLayout.inp_dark_select : hubLayout.inp_select}`} style={{ width: '15rem' }}>
                    <select className={`${darkAtom ? hubLayout.inp_dark_select : hubLayout.inp_select} ${errors.sales_id && hubLayout.border_red}`} value={contract.sales_id || ""}
                      {...register(`sales_id`, {
                        required: {
                          value: true,
                          message: '영업자 ID를 선택하세요.',
                        },
                        onChange: (e) => {
                          const selectedOption = e.target.selectedOptions[0];
                          const mobile = selectedOption.getAttribute('data-mobile');
                          handleChange(e);
                          setSalesMobile(mobile || '');
                        }
                      })}
                    >
                      <option value='' data-mobile=''>--선택해주세요--</option>
                      {salesList.map((sales, idx) => (
                        <option key={`sales_${idx}`} value={sales.id} data-mobile={decryptAES256(sales.mobile, REACT_APP_SECRET_KEY!, REACT_APP_SECRET_IV!)}>{sales.id}</option>
                      ))}
                    </select>
                  </div>
                  {errors.sales_id && <p className={`${hubLayout.tx_red}`}>{String(errors.sales_id.message)}</p>}
                </dd>
                <dt style={{ width: '10rem' }}><label>영업자 연락처</label></dt>
                <dd>
                  <input type='text' name="sales_mobile" defaultValue={salesMobile} className={`${hubLayout.margin_right_10}`} placeholder='자동 입력' readOnly />
                </dd>
              </dl>
              <dl>
                <dt><label>계약일자</label></dt>
                <dd>
                  <input type="date" name='contract_date' value={contract.contract_date} onChange={(e) => handleChange(e)} style={{ width: '10rem' }} />
                  {errors.contract_date && <p className={`${hubLayout.tx_red}`}>{String(errors.contract_date.message)}</p>}
                </dd>
                <dt><label>시작일자</label></dt>
                <dd>
                  <input type="date" name='start_date' value={contract.start_date} onChange={(e) => handleChange(e)} style={{ width: '10rem' }} />
                  {errors.start_date && <p className={`${hubLayout.tx_red}`}>{String(errors.start_date.message)}</p>}
                </dd>
                <dt><label>계약기간</label></dt>
                <dd>
                  <div className={`${darkAtom ? hubLayout.inp_dark_select : hubLayout.inp_select}`} style={{ width: '10rem' }}>
                    <select className={`${darkAtom ? hubLayout.inp_dark_select : hubLayout.inp_select}`} value={contract.term || "1"}
                      {...register(`term`, {
                        required: true,
                        onChange: handleChange
                      })}
                    >
                      {Array.from({ length: 5 }, (_, idx) => (
                        <option key={`term_${idx}`} value={idx + 1}>{idx + 1}년</option>
                      ))}
                    </select>
                  </div>
                  {errors.contract_date && <p className={`${hubLayout.tx_red}`}>{String(errors.contract_date.message)}</p>}
                </dd>
                <dt><label>관할 지사</label></dt>
                <dd>
                  <div className={`${darkAtom ? hubLayout.inp_dark_select : hubLayout.inp_select}`} style={{ width: '10rem' }}>
                    <select className={`${darkAtom ? hubLayout.inp_dark_select : hubLayout.inp_select}`} value={contract.jisa_code}
                      {...register(`jisa_code`, {
                        required: true,
                        onChange: handleChange
                      })}
                    >
                      {jisaList.map((jisa, idx) => (
                        <option key={`jisa_${idx}`} value={jisa.jisa_code}>{jisa.jisa_name}</option>
                      ))}
                    </select>
                  </div>
                  {errors.jisa_code && <p className={`${hubLayout.tx_red}`}>{String(errors.jisa_code.message)}</p>}
                </dd>
              </dl>
              <dl>
                <dt><label>계약상품</label></dt>
                <dd>
                  <div className={`${darkAtom ? hubLayout.inp_dark_select : hubLayout.inp_select}`} style={{ width: '10rem' }}>
                    <select className={`${darkAtom ? hubLayout.inp_dark_select : hubLayout.inp_select}`} value={contract.contract_product}
                      {...register(`contract_product`, {
                        required: true,
                        onChange: handleChange
                      })}
                    >
                      <option value={`기본형`}>기본형</option>
                      <option value={`임대형`}>임대형</option>
                    </select>
                  </div>
                  {errors.contract_product && <p className={`${hubLayout.tx_red}`}>{String(errors.contract_product.message)}</p>}
                </dd>
                <dt><label>관제서비스</label></dt>
                <dd>
                  <div className={`${darkAtom ? hubLayout.inp_dark_select : hubLayout.inp_select}`} style={{ width: '10rem' }}>
                    <select className={`${darkAtom ? hubLayout.inp_dark_select : hubLayout.inp_select}`} value={(contract.service_code as any)?.control_service}
                      {...register(`control_service`, {
                        required: true,
                        onChange: handleChange
                      })}
                    >
                      <option value={0}>자가방범</option>
                      <option value={1}>무인관제</option>
                      <option value={2}>유인관제</option>
                    </select>
                  </div>
                  {errors.control_service && <p className={`${hubLayout.tx_red}`}>{String(errors.control_service.message)}</p>}
                </dd>
              </dl>
            </div>
          </div>

          <TitleAndBtnContainer>
            <div>
              <p className='title-h2'>
                <span>* 녹화기 구성</span>
              </p>
            </div>
            <div>
              <button type="button" className={`${hubLayout.btn} ${hubLayout.width_100} ${hubLayout.medium} ${hubLayout.blue}`} onClick={handleAddRecorder}>녹화기 추가</button>
            </div>
          </TitleAndBtnContainer>
          <div className={`${hubLayout.acc__cnt}`}>
            <div className={`${hubLayout.acc_item_info}`}>
              {/* 녹화기 */}
              {contractProductCount.recorder && contractProductCount.recorder.length > 0 ?
                contractProductCount.recorder.map((el: any, cpIdx: number) => (
                  <dl key={`cpRecorder_${cpIdx}`}>
                    <dt><label>Model</label></dt>
                    <dd>
                      <div className={`${darkAtom ? hubLayout.inp_dark_select : hubLayout.inp_select}`} style={{ width: '12rem' }}>
                        <select className={`${darkAtom ? hubLayout.inp_dark_select : hubLayout.inp_select}`} value={el.model}
                          {...register(`recorderModel_${cpIdx}`, {
                            required: true,
                            onChange: (e:any) => {
                              handleModelChangeRecorder(e, cpIdx);
                            }
                          })}
                        >
                        <option value=''>--Model 선택--</option>
                          {modelList.map((model, idx) => (
                            <option key={`recorderModel_${idx}`} value={model.name}>{model.name}</option>
                          ))}
                        </select>
                      </div>
                    </dd>
                    <dt><label>사용 대수</label></dt>
                    <dd>
                      <input type="text" placeholder="1" value={el.use}  className={`${errors[`recorderUse_${cpIdx}`] && hubLayout.border_red}`} style={{ width: '50%' }} 
                        {...register(`recorderUse_${cpIdx}`, {
                          required: {
                            value: true,
                            message: '숫자 입력',
                          },
                          onChange: (e:any) => {
                            handleUseChangeRecorder(e, cpIdx);
                          }
                        })}
                      />
                    </dd>
                    <div>
                      <button type="button" className={`${hubLayout.btn} ${hubLayout.width_100} ${hubLayout.medium} ${hubLayout.pink}`} onClick={() => handleDeleteRecorder(cpIdx)}>녹화기 삭제</button>
                    </div>
                  </dl>
                ))
              :
                <p style={{ color: "#fff", padding: "10px 15px 0px" }}>녹화기 없음</p>
              }
            </div>
          </div>

          <TitleAndBtnContainer>
            <div>
              <p className='title-h2'>
                <span>* 카메라 구성</span>
              </p>
            </div>
            <div>
              <button type="button" className={`${hubLayout.btn} ${hubLayout.width_100} ${hubLayout.medium} ${hubLayout.blue}`} onClick={handleAddCamera}>카메라 추가</button>
            </div>
          </TitleAndBtnContainer>
          <div className={`${hubLayout.acc__cnt}`}>
            <div className={`${hubLayout.acc_item_info}`}>
              {/* 카메라 */}
              {contractProductCount.camera && contractProductCount.camera.length > 0 ?
                contractProductCount.camera.map((el: any, cpIdx: number) => (
                  <dl key={`cpCamera_${cpIdx}`}>
                    <dt><label>Model</label></dt>
                    <dd>
                      <div className={`${darkAtom ? hubLayout.inp_dark_select : hubLayout.inp_select}`} style={{ width: '12rem' }}>
                        <select className={`${darkAtom ? hubLayout.inp_dark_select : hubLayout.inp_select}`} value={el.model}
                          {...register(`cameraModel_${cpIdx}`, {
                            required: true,
                            onChange: (e:any) => {
                              handleModelChangeCamera(e, cpIdx);
                            }
                          })}
                        >
                        <option value=''>--Model 선택--</option>
                          {cameraModelList.map((cameraModel, idx) => (
                            <option key={`cameraModel_${idx}`} value={cameraModel.name}>{cameraModel.name}</option>
                          ))}
                        </select>
                      </div>
                    </dd>
                    <dt><label>사용 대수</label></dt>
                    <dd>
                      <input type="text" placeholder="1" value={el.use}  className={`${errors[`cameraUse_${cpIdx}`] && hubLayout.border_red}`} style={{ width: '50%' }} 
                        {...register(`cameraUse_${cpIdx}`, {
                          required: {
                            value: true,
                            message: '숫자 입력',
                          },
                          onChange: (e:any) => {
                            handleUseChangeCamera(e, cpIdx);
                          }
                        })}
                      />
                    </dd>
                    <div>
                      <button type="button" className={`${hubLayout.btn} ${hubLayout.width_100} ${hubLayout.medium} ${hubLayout.pink}`} onClick={() => handleDeleteCamera(cpIdx)}>카메라 삭제</button>
                    </div>
                  </dl>
                ))
              :
                <p style={{ color: "#fff", padding: "10px 15px 0px" }}>카메라 없음</p>
              }
            </div>
          </div>

          <TitleAndBtnContainer>
            <div>
              <p className='title-h2'>
                <span>* 결제 정보</span>
              </p>
            </div>
          </TitleAndBtnContainer>
          <div className={`${hubLayout.acc__cnt}`}>
            <div className={`${hubLayout.acc_item_info}`}>
              <dl>
                <dt><label>계약금액(월/VAT포함)</label></dt>
                <dd>
                  <input type='text' value={contract.price.toLocaleString("ko-KR") || ""}
                    {...register(`price`, {
                      required: {
                        value: true,
                        message: '계약금액(월/VAT포함)을 입력하세요',
                      },
                      onChange: handleToNumericValue
                    })}
                  />
                  {errors.price && <p className={`${errors.price && hubLayout.tx_red}`}>{String(errors.price?.message)}</p>}
                </dd>
                <dt><label>결제방식</label></dt>
                <dd>
                  <span className={`${hubLayout.margin_right_10}`}>
                    <input type="radio" className={`${hubLayout.radio}`} value="A" id='inp-payment-type-a' checked={contract.payment_type === 'A'} style={{ top: '7px' }}
                    {...register(`payment_type`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                    <label htmlFor='inp-payment-type-a' style={{ color: darkAtom ? '#fff' : '' }}>카드결제</label>
                  </span>
                  <span className={`${hubLayout.margin_right_10}`}>
                    <input type="radio" className={`${hubLayout.radio}`} value="B" id='inp-payment-type-b' checked={contract.payment_type === 'B'} style={{ top: '7px' }}
                    {...register(`payment_type`, {
                        required: true,
                        onChange: handleChange
                      })}
                    />
                    <label htmlFor='inp-payment-type-b' style={{ color: darkAtom ? '#fff' : '' }}>자동이체</label>
                  </span>
                </dd>
              </dl>
              {contract.payment_type === 'B' ?
                <dl>
                  <dt><label>은행</label></dt>
                  <dd>
                    <div className={`${darkAtom ? hubLayout.inp_dark_select : hubLayout.inp_select}`} style={{ width: '14.5rem' }}>
                      <select className={`${darkAtom ? hubLayout.inp_dark_select : hubLayout.inp_select}`} value={contract.bank_name || ""}
                        {...register(`bank_name`, {
                          required: {
                            value: true,
                            message: '은행을 선택하세요',
                          },
                          onChange: handleChange
                        })}
                      >
                        <option value=''>--은행 선택--</option>
                        {bankList.map((bank: { code: string, name: string }) => (
                          <option key={bank.code} value={bank.name}>{bank.name}</option>
                        ))}
                      </select>
                    </div>
                    {errors.bank_name && <p className={`${errors.bank_name && hubLayout.tx_red}`}>{String(errors.bank_name?.message)}</p>}
                  </dd>
                  <dt><label>계좌번호</label></dt>
                  <dd>
                    <input type='text' value={contract.bank_number || ""} className={`${errors.bank_number && hubLayout.border_red}`}
                      {...register(`bank_number`, {
                        required: {
                          value: true,
                          message: '계좌번호를 입력하세요',
                        },
                        onChange: handleToNumericValue
                      })}
                    />
                    {errors.bank_number && <p className={`${errors.bank_number && hubLayout.tx_red}`}>{String(errors.bank_number?.message)}</p>}
                  </dd>
                </dl>
              : contract.payment_type === 'A' &&
                <dl>
                  <dt><label>카드업체</label></dt>
                  <dd>
                  <div className={`${darkAtom ? hubLayout.inp_dark_select : hubLayout.inp_select}`} style={{ width: '14.5rem' }}>
                    <select className={`${darkAtom ? hubLayout.inp_dark_select : hubLayout.inp_select}`} value={contract.card_company || ""}
                      {...register(`card_company`, {
                        required: {
                          value: true,
                          message: '카드사를 선택하세요',
                        },
                        onChange: handleChange
                      })}
                    >
                      <option value=''>--카드업체 선택--</option>
                      {cardCompanyList.map((card: { code: string, name: string }) => (
                        <option key={card.code} value={card.name}>{card.name}</option>
                      ))}
                    </select>
                  </div>
                    {errors.card_company && <p className={`${errors.card_company && hubLayout.tx_red}`}>{String(errors.card_company?.message)}</p>}
                  </dd>
                  <dt><label>카드번호</label></dt>
                  <dd>
                    <input type='text' value={contract.card_number || ""} placeholder='카드번호' className={`${errors.card_number && hubLayout.border_red}`}
                      {...register(`card_number`, {
                        required: {
                          value: true,
                          message: '카드번호를 입력하세요',
                        },
                        onChange: handleToNumericValue
                      })}
                    />
                    {errors.card_number && <p className={`${errors.card_number && hubLayout.tx_red}`}>{String(errors.card_number?.message)}</p>}
                  </dd>
                </dl>
              }
            </div>
          </div>
        </Container>
      }
    </>
  )
}

export default ContractRegister;